import { useState, useEffect } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { loyaltyService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { BrainCircuit, Search, Loader2, AlertTriangle, Crown, Sparkles, Bot, TrendingUp } from "lucide-react";

const RISK_COLOR = { Low: "text-emerald-600 bg-emerald-500/10", Medium: "text-amber-600 bg-amber-500/10", High: "text-rose-600 bg-rose-500/10" };
const TWIN_BARS = [
  ["loyaltyStrength", "Loyalty Strength"], ["rewardUtilization", "Reward Utilization"],
  ["tierProgression", "Tier Progression"], ["redemptionBehavior", "Redemption Behavior"],
  ["engagementQuality", "Engagement Quality"],
];

function Gauge({ value }) {
  const color = value >= 70 ? "#10b981" : value >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <div className="relative h-28 w-28">
      <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
        <path d="M18 2a16 16 0 110 32 16 16 0 010-32" fill="none" stroke="currentColor" className="text-muted" strokeWidth="3" />
        <path d="M18 2a16 16 0 110 32 16 16 0 010-32" fill="none" stroke={color} strokeWidth="3" strokeDasharray={`${value}, 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-2xl font-bold text-foreground">{value}</span><span className="text-xs text-muted-foreground">/100</span></div>
    </div>
  );
}

export default function AiLoyaltyPage() {
  const { format } = useCurrency();
  const [cust, setCust] = useState("");
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [twin, setTwin] = useState(null);
  // copilot
  const [questions, setQuestions] = useState([]);
  const [copilot, setCopilot] = useState(null);
  const [copilotBusy, setCopilotBusy] = useState(false);

  useEffect(() => { loyaltyService.copilotQuestions().then((d) => setQuestions(d.questions)).catch(() => {}); }, []);

  const analyze = async () => {
    if (!cust.trim()) { toast.error("Enter a customer ID"); return; }
    setLoading(true); setInsights(null); setTwin(null);
    try {
      const [i, t] = await Promise.all([loyaltyService.aiInsights(cust, true), loyaltyService.digitalTwin(cust)]);
      setInsights(i); setTwin(t);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const ask = async (q) => {
    setCopilotBusy(true); setCopilot(null);
    try { setCopilot(await loyaltyService.copilotAsk(q.id)); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setCopilotBusy(false); }
  };

  return (
    <div data-testid="ai-loyalty-page">
      <PageHeader title="AI Loyalty Intelligence" subtitle="AI Loyalty Agent · Loyalty Digital Twin · Copilot" />

      {/* Customer analyzer */}
      <div className="bg-card border border-border rounded-xl p-4 mb-6" data-testid="ai-analyzer">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs text-muted-foreground mb-1">Customer ID</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input value={cust} onChange={(e) => setCust(e.target.value)} onKeyDown={(e) => e.key === "Enter" && analyze()} placeholder="e.g. cust-123" className="w-full rounded-lg border border-border bg-background pl-9 pr-3 py-2 text-sm" data-testid="ai-customer-input" />
            </div>
          </div>
          <button onClick={analyze} disabled={loading} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="ai-analyze-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BrainCircuit className="h-4 w-4" />} Analyze
          </button>
        </div>
      </div>

      {insights && insights.hasData && (
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Agent insights */}
          <div className="bg-card border border-border rounded-xl p-5" data-testid="ai-insights-card">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Bot className="h-4.5 w-4.5 text-primary" /> AI Loyalty Agent</h3>
            <div className="flex items-center gap-5 mb-4">
              <Gauge value={insights.loyaltyScore} />
              <div className="space-y-2">
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${RISK_COLOR[insights.retentionRisk.level]}`} data-testid="ai-risk-badge">
                  <AlertTriangle className="h-3.5 w-3.5" /> {insights.retentionRisk.level} retention risk
                </div>
                <p className="text-xs text-muted-foreground">{insights.retentionRisk.reason}</p>
                {insights.vip && <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-violet-600 bg-violet-500/10"><Crown className="h-3.5 w-3.5" /> VIP customer</div>}
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex gap-2"><Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" /><div><p className="font-medium text-foreground">Next Best Reward</p><p className="text-muted-foreground">{insights.nextBestReward}</p></div></div>
              <div className="flex gap-2"><TrendingUp className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /><div><p className="font-medium text-foreground">Tier Upgrade</p><p className="text-muted-foreground">{insights.tierUpgrade?.reason || "—"}{insights.tierUpgrade?.recommend ? ` → recommend ${insights.tierUpgrade.tierName}` : ""}</p></div></div>
              <div className="flex gap-2"><Bot className="h-4 w-4 text-sky-500 mt-0.5 shrink-0" /><div><p className="font-medium text-foreground">Reward Recommendation</p><p className="text-muted-foreground">{insights.rewardRecommendation}</p></div></div>
            </div>
            {insights.aiNarrative && (
              <div className="mt-4 rounded-lg bg-muted/40 border border-border p-3 text-xs" data-testid="ai-narrative">
                {insights.aiNarrative.enabled
                  ? <p className="text-foreground">{insights.aiNarrative.text}</p>
                  : <p className="text-muted-foreground italic">AI narrative is available when your OpenAI Business key is configured at deployment.</p>}
              </div>
            )}
          </div>

          {/* Digital twin */}
          <div className="bg-card border border-border rounded-xl p-5" data-testid="ai-twin-card">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Sparkles className="h-4.5 w-4.5 text-primary" /> Loyalty Digital Twin</h3>
            <div className="space-y-3">
              {TWIN_BARS.map(([key, label]) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-foreground">{label}</span><span className="font-semibold text-muted-foreground">{twin?.[key] ?? 0}</span></div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${twin?.[key] ?? 0}%` }} /></div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div><p className="text-xs text-muted-foreground">Lifetime Earned</p><p className="font-bold text-foreground">{(twin?.summary?.lifetimeEarned || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-muted-foreground">Redeemed</p><p className="font-bold text-foreground">{(twin?.summary?.lifetimeRedeemed || 0).toLocaleString()}</p></div>
              <div><p className="text-xs text-muted-foreground">Txns</p><p className="font-bold text-foreground">{twin?.summary?.transactionCount || 0}</p></div>
            </div>
          </div>
        </div>
      )}

      {insights && !insights.hasData && (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground mb-6" data-testid="ai-no-data">No loyalty data for this customer yet.</div>
      )}

      {/* Copilot */}
      <div className="bg-card border border-border rounded-xl p-5" data-testid="copilot-card">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Bot className="h-4.5 w-4.5 text-primary" /> Loyalty Copilot</h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {questions.map((q) => (
            <button key={q.id} onClick={() => ask(q)} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted transition-colors" data-testid={`copilot-q-${q.id}`}>{q.label}</button>
          ))}
        </div>
        {copilotBusy && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div>}
        {copilot && (
          <div className="rounded-lg bg-muted/40 border border-border p-4" data-testid="copilot-answer">
            <p className="text-sm font-medium text-foreground mb-3">{copilot.answer}</p>
            {Array.isArray(copilot.data) ? (
              <div className="space-y-1.5">
                {copilot.data.length === 0 ? <p className="text-xs text-muted-foreground">No matching records.</p> : copilot.data.map((row, i) => (
                  <div key={i} className="flex flex-wrap gap-x-3 text-xs text-foreground border-b border-border pb-1.5 last:border-0">
                    {Object.entries(row).map(([k, v]) => <span key={k}><span className="text-muted-foreground">{k}:</span> {String(v)}</span>)}
                  </div>
                ))}
              </div>
            ) : copilot.data ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(copilot.data).map(([k, v]) => (
                  <div key={k}><p className="text-xs text-muted-foreground">{k}</p><p className="text-sm font-semibold text-foreground">{k.toLowerCase().includes("liability") || k.toLowerCase().includes("cashback") ? format(v) : String(v)}</p></div>
                ))}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
